import { RiInformationLine } from '@remixicon/react';
import { differenceInHours, format } from 'date-fns';
import { useEffect, useState } from 'react';
import {
	choosePlatform,
	countRealTime,
	CurrencyFormatterNonDecimal,
	showWalletAndBalance,
	verifyDateTournament,
} from '../../../helpers/utils';
import { TournamentData } from '../../../types/types';
import { FutProIcon } from '../../Icons/FutProIcon';
import { PickProIcon } from '../../Icons/PickProIcon';
import Tooltip from '../../Tooltip';

interface IProps {
	data: TournamentData;
}

export const EnrollTournamentNew = ({ data }: IProps) => {
	const [remainingTime, setRemainingTime] = useState(
		countRealTime(data.entryEventAt)
	);
	const [showCounter, setShowCounter] = useState(false);

	useEffect(() => {
		if (data?.tournamentStatus === 0) {
			const now = new Date();
			const diffHours = differenceInHours(
				new Date(data.entryEventAt || ''),
				now
			);
			const closingSoon = diffHours < 4;
			setShowCounter(closingSoon);

			const interval = setInterval(() => {
				const time = countRealTime(data.entryEventAt);
				setRemainingTime(time);
				if (time.isFinished) {
					clearInterval(interval);
				}
			}, 1000);

			return () => clearInterval(interval);
		}
	}, []);

	return (
		<div className="w-full rounded-lg pb-3 bg-custom-gray shadow-md">
			<div className="flex w-full flex-row rounded-t-lg items-center justify-between px-3 py-3">
				<div className="flex flex-col justify-start items-start">
					<div className="text-custom-gray-11 text-base font-semibold font-bahnschrift">
						{data.leagueName}
					</div>
					<div className="text-custom-gray-11 text-sm font-light font-bahnschrift">
						{data.title}
					</div>
				</div>

				<div className="flex flex-col justify-end items-end">
					<div className="flex flex-col justify-end items-end gap-1">
						<div className="p-2 bg-black/10 rounded-lg flex-col justify-center items-center gap-2 inline-flex">
							<div className="flex-col justify-start items-start gap-1.5 flex">
								<div className="text-custom-gray-12 text-xs font-semibold font-bahnschrift">
									{data.tournamentStatus !== 0
										? format(
												data.entryEventAt,
												'dd/MM HH:mm'
										  )
										: verifyDateTournament(
												data.entryEventAt.replace(
													'Z',
													''
												)
										  )}
								</div>
							</div>
						</div>

						{showCounter && (
							<div className="flex flex-row gap-1 items-center justify-center bg-primary rounded-[80px] px-2 py-1">
								<span className="text-white text-xs font-semibold font-bahnschrift">
									Fecha em:
								</span>
								<span className="text-white text-xs font-semibold font-bahnschrift">
									{`${remainingTime.hours
										.toString()
										.padStart(2, '0')}h:` +
										`${remainingTime.minutes
											.toString()
											.padStart(2, '0')}m`}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="flex w-full flex-row justify-between px-3.5 pb-3">
				<div className="grid grid-cols-2 justify-between w-full gap-2">
					<div className="flex flex-row justify-between">
						<div className="flex flex-col justify-start items-start">
							<Tooltip
								icon={
									<>
										<div className="text-left flex w-full justify-center items-center gap-1 text-custom-gray-11 text-sm font-light font-bahnschrift pb-1">
											Premiação
											<RiInformationLine
												color="#636363"
												className="w-4 h-4 mt-0.5"
											/>
										</div>
										<div>
											{Number(data.prizeAmt) === 0 &&
											Number(data.prizeAmtBonus) === 0 ? (
												<div className="text-custom-gray-11 text-xs leading-3 font-bahnschrift font-semibold uppercase">
													Sem Premiação
												</div>
											) : (
												<>
													{Number(data.prizeAmt) !==
													0 ? (
														<span className="text-custom-gray-11 text-lg leading-3 font-semibold font-bahnschrift flex justify-end items-center gap-1">
															{showWalletAndBalance() ? (
																'R$ '
															) : choosePlatform() ? (
																<PickProIcon
																	style="w-3.5 h-3.5 mr-1"
																	color="#636363"
																/>
															) : (
																<FutProIcon
																	style="w-3.5 h-3.5 mr-1"
																	color="#636363"
																/>
															)}
															{CurrencyFormatterNonDecimal(
																Number(
																	data.prizeAmt
																)
															)}
														</span>
													) : (
														Number(
															data.prizeAmtBonus
														) !== 0 && (
															<span className="text-custom-gray-11 text-lg leading-3 font-semibold font-bahnschrift flex justify-end items-center gap-1">
																{'B$ '}
																{CurrencyFormatterNonDecimal(
																	Number(
																		data.prizeAmtBonus
																	)
																)}
															</span>
														)
													)}
													{Number(
														data.prizeAmtBonus
													) !== 0 &&
														Number(
															data.prizeAmt
														) !== 0 && (
															<span className="text-custom-gray-11 text-xs font-semibold font-bahnschrift flex justify-end items-end gap-1">
																+ {'B$ '}
																{CurrencyFormatterNonDecimal(
																	Number(
																		data.prizeAmtBonus
																	)
																)}
															</span>
														)}
												</>
											)}
										</div>
									</>
								}
								infoIcon="warning"
								title="Premiação"
								message={
									<>
										{Number(data.prizeAmt) === 0 &&
											Number(data.prizeAmtBonus) ===
												0 && (
												<p className="text-[#535861] text-sm font-light font-bahnschrift leading-tight">
													Este torneio não distribui
													prêmios.
												</p>
											)}
										{Number(data.prizeAmt) !== 0 &&
											Number(data.prizeAmtBonus) !==
												0 && (
												<p className="text-[#535861] text-sm font-light font-bahnschrift leading-tight">
													Este torneio oferece R${' '}
													{CurrencyFormatterNonDecimal(
														Number(data.prizeAmt)
													)}
													, que serão distribuídos
													entre os ganhadores, além de
													B${' '}
													{CurrencyFormatterNonDecimal(
														Number(
															data.prizeAmtBonus
														)
													)}{' '}
													em bônus.
												</p>
											)}

										{Number(data.prizeAmt) !== 0 &&
											Number(data.prizeAmtBonus) ===
												0 && (
												<p className="text-[#535861] text-sm font-light font-bahnschrift leading-tight">
													Este torneio oferece R$
													{CurrencyFormatterNonDecimal(
														Number(data.prizeAmt)
													)}
													, que serão distribuídos
													entre os ganhadores.
												</p>
											)}
										{Number(data.prizeAmt) === 0 &&
											Number(data.prizeAmtBonus) !==
												0 && (
												<p className="text-[#535861] text-sm font-light font-bahnschrift leading-tight">
													Este torneio oferece B${' '}
													{CurrencyFormatterNonDecimal(
														Number(
															data.prizeAmtBonus
														)
													)}
													, em bônus que serão
													distribuídos entre os
													ganhadores
												</p>
											)}
									</>
								}
							/>
						</div>
					</div>
					<div className="flex flex-row justify-end">
						<div className="flex flex-col justify-start items-start">
							<div className="text-custom-gray-11 text-sm font-light font-bahnschrift ">
								Entrada
							</div>

							{Number(data.entryAmt) === 0 ? (
								<div className="text-custom-gray-11 text-sm leading-3 animate-blink font-bahnschrift font-semibold uppercase">
									Gratuito
								</div>
							) : (
								<span className="text-custom-gray-11 text-base leading-4 font-semibold font-bahnschrift flex justify-center items-center gap-1">
									{showWalletAndBalance() ? (
										'R$ '
									) : choosePlatform() ? (
										<PickProIcon
											style="w-3.5 h-3.5 mr-1"
											color="#636363"
										/>
									) : (
										<FutProIcon
											style="w-3.5 h-3.5 mr-1"
											color="#636363"
										/>
									)}
									{CurrencyFormatterNonDecimal(
										Number(data.entryAmt)
									)}
								</span>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
